import {Actions, Avatar, DataStatus, Flag, Label} from 'platform/components';
import {Grid, GridItem, HStack, Text, VStack} from 'platform/foundation';

import {isPositive} from 'ramda-adjunct';

import {useGetManufacturerQuery} from '@dms/api/core';
import {NormalizedFastTrackItem, useGetHandlingUnitQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

export interface ReceiveNoteInformationRowProps extends RequiredTestIdProps {
  receiveNoteItem: NormalizedFastTrackItem;
  onToggleCollapse: () => void;
  onAccept: () => void;
  isCollapsed: boolean;
  isReceiveNoteCompleted: boolean;
}

export function ReceiveNoteInformationRow(props: ReceiveNoteInformationRowProps) {
  const {data: manufacturer, isLoading: isManufacturerLoading} = useGetManufacturerQuery(
    {manufacturerId: props.receiveNoteItem.manufacturerId},
    {skip: !props.receiveNoteItem.manufacturerId}
  );

  const {data: handlingUnit, isLoading: isHandlingUnitLoading} = useGetHandlingUnitQuery(
    {id: props.receiveNoteItem.unitId},
    {skip: !props.receiveNoteItem.unitId}
  );

  const isLoading = isManufacturerLoading || isHandlingUnitLoading;
  const colorScheme = isPositive(props.receiveNoteItem.quantityLeft) ? 'green' : 'red';

  return (
    <DataStatus isLoading={isLoading}>
      <Grid columns={10}>
        <GridItem span={1}>
          <Avatar
            icon={props.receiveNoteItem.isAccepted ? 'navigation/check' : 'devices/access_time'}
            size="default"
            color={
              props.receiveNoteItem.isAccepted ? 'palettes.green.30.100' : 'palettes.orange.30.100'
            }
          />
        </GridItem>

        <GridItem span={2}>
          <VStack>
            <Label>{i18n.t('entity.warehouse.labels.catalogNumber')}</Label>
            <Text>{props.receiveNoteItem.catalogNumber}</Text>
          </VStack>
        </GridItem>
        <GridItem span={2}>
          <VStack>
            <Label>{i18n.t('entity.warehouse.labels.name')}</Label>
            <Text>{props.receiveNoteItem.name}</Text>
          </VStack>
        </GridItem>
        <GridItem span={2}>
          <VStack>
            <Label>{i18n.t('entity.warehouse.labels.manufacturer')}</Label>
            <Text>{manufacturer?.name}</Text>
          </VStack>
        </GridItem>
        <GridItem span={1}>
          <VStack>
            <Label>{i18n.t('entity.warehouse.labels.quantity')}</Label>
            <Text>
              {props.receiveNoteItem.quantity} {handlingUnit?.name}
            </Text>
          </VStack>
        </GridItem>
        <GridItem span={1}>
          <VStack width={18}>
            <Label>{i18n.t('entity.warehouse.labels.quantityLeft')}</Label>
            <Flag
              isFullWidth
              colorScheme={colorScheme}
              label={
                String(props.receiveNoteItem.quantityLeft) + String(handlingUnit?.name as string)
              }
            />
          </VStack>
        </GridItem>
        <GridItem span={1}>
          <HStack justify="flex-end">
            <VStack align="flex-start">
              <Actions
                actions={[
                  {
                    type: 'iconButtonWithTooltip',
                    icon: props.receiveNoteItem.isAccepted
                      ? 'action/replay'
                      : 'action/approval_delegation',
                    tooltip: {
                      label: props.receiveNoteItem.isAccepted
                        ? i18n.t('entity.warehouse.labels.unaccept')
                        : i18n.t('entity.warehouse.labels.accept'),
                    },
                    onClick: () => {
                      props.onAccept();
                    },
                    isDisabled: props.isReceiveNoteCompleted,
                  },
                  {
                    type: 'iconButtonWithTooltip',
                    icon: props.isCollapsed ? 'navigation/expand_more' : 'navigation/expand_less',
                    tooltip: {
                      label: props.isCollapsed
                        ? i18n.t('entity.warehouse.labels.expandAll')
                        : i18n.t('entity.warehouse.labels.collapseAll'),
                    },
                    onClick: () => {
                      props.onToggleCollapse();
                    },
                    'data-testid': suffixTestId('toggleCollapse', props),
                  },
                ]}
              />
            </VStack>
          </HStack>
        </GridItem>
      </Grid>
    </DataStatus>
  );
}
